import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createSession,
  listSessions,
  resetSession,
  verifyAdmin,
  type PersistentSession,
} from "./api";
import { saveFacilitatorToken } from "./Landing";
import { DEFAULT_WORKSHOP } from "@shared/seed";

const field: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: "1.5px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  fontSize: 13,
  color: "var(--fg-inv)",
  background: "var(--surface)",
  fontFamily: "var(--font)",
};

const card: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "var(--radius-lg)",
  padding: 28,
  width: 460,
  maxWidth: "92vw",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  textAlign: "left",
  marginTop: 16,
};

const eyebrow: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--violet)",
};

export function Admin() {
  const [unlocked, setUnlocked] = useState(!!localStorage.getItem("adminKey"));

  if (!unlocked) return <AdminGate onUnlock={() => setUnlocked(true)} />;
  return <AdminPanel onLock={() => setUnlocked(false)} />;
}

function AdminGate({ onUnlock }: { onUnlock: () => void }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      if (await verifyAdmin(key.trim())) {
        localStorage.setItem("adminKey", key.trim());
        onUnlock();
      } else {
        setErr("That admin key was rejected.");
      }
    } catch {
      setErr("Could not verify the key. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="center-screen">
      <h1>Admin</h1>
      <p>Enter the admin key to manage sessions.</p>
      <div style={card}>
        <div style={eyebrow}>Admin access</div>
        <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>
          Admin key
          <input
            style={{ ...field, marginTop: 4 }}
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && key.trim() && submit()}
            autoFocus
          />
        </label>
        {err && <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}
        <button className="btn-primary" onClick={submit} disabled={busy || !key.trim()}>
          {busy ? "Checking…" : "Unlock →"}
        </button>
      </div>
      <Link to="/" style={{ marginTop: 18, fontSize: 12, color: "var(--muted)", textDecoration: "none" }}>
        ← Back to start
      </Link>
    </div>
  );
}

function AdminPanel({ onLock }: { onLock: () => void }) {
  const nav = useNavigate();

  // ── Create new session ──────────────────────────────────────────────────────
  const [client, setClient] = useState("");
  const [sessionLabel, setSessionLabel] = useState(DEFAULT_WORKSHOP.sessionLabel);
  const [meetingLink, setMeetingLink] = useState("");
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const create = async () => {
    setBusy(true);
    setErr("");
    try {
      const { session, facilitatorToken } = await createSession({
        client: client || DEFAULT_WORKSHOP.client,
        sessionLabel,
        meetingLink: meetingLink || null,
        expectedParticipants: expected ? parseInt(expected, 10) : null,
      });
      saveFacilitatorToken(session.id, facilitatorToken);
      nav(`/s/${session.id}`);
    } catch (e) {
      setErr(String((e as Error).message));
      setBusy(false);
    }
  };

  // ── Manage persistent sessions ──────────────────────────────────────────────
  const [sessions, setSessions] = useState<PersistentSession[]>([]);
  const [confirmId, setConfirmId] = useState("");
  const [resetting, setResetting] = useState("");
  const [resetMsg, setResetMsg] = useState("");

  useEffect(() => {
    listSessions().then(setSessions).catch(() => {});
  }, []);

  const doReset = async (id: string, label: string) => {
    setResetting(id);
    setResetMsg("");
    try {
      await resetSession(id);
      setResetMsg(`${label} was hard reset.`);
    } catch (e) {
      setResetMsg(String((e as Error).message));
    } finally {
      setResetting("");
      setConfirmId("");
    }
  };

  const lock = () => {
    localStorage.removeItem("adminKey");
    onLock();
  };

  return (
    <div className="center-screen" style={{ paddingTop: 40, paddingBottom: 40, justifyContent: "flex-start" }}>
      <h1>Admin</h1>
      <p>Create sessions and run hard resets. Regular users never see this screen.</p>

      {/* Manage existing sessions */}
      <div style={card}>
        <div style={eyebrow}>Sessions</div>
        {sessions.length === 0 && <div style={{ fontSize: 12, color: "var(--muted)" }}>Loading…</div>}
        {sessions.map((s) => {
          const label = s.kind === "sandbox" ? "Sandbox" : "Live Session";
          return (
            <div
              key={s.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 0",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "var(--fg-inv)", fontSize: 14 }}>{label}</div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>Code {s.joinCode}</div>
              </div>
              <button
                className="btn-secondary"
                style={{ fontSize: 12, padding: "6px 12px" }}
                onClick={() => nav(`/s/${s.id}`)}
              >
                Open
              </button>
              {confirmId === s.id ? (
                <button
                  className="btn-danger"
                  style={{ fontSize: 12, padding: "6px 12px" }}
                  onClick={() => doReset(s.id, label)}
                  disabled={resetting === s.id}
                >
                  {resetting === s.id ? "Resetting…" : "Confirm reset"}
                </button>
              ) : (
                <button
                  className="btn-secondary"
                  style={{ fontSize: 12, padding: "6px 12px", color: "var(--red)", borderColor: "var(--red)" }}
                  onClick={() => { setConfirmId(s.id); setResetMsg(""); }}
                >
                  Hard reset
                </button>
              )}
            </div>
          );
        })}
        {confirmId && (
          <div style={{ fontSize: 11, color: "var(--muted)" }}>
            Hard reset wipes all responses, evidence, participants, chat and imports for this session.
          </div>
        )}
        {resetMsg && <div style={{ fontSize: 12, color: "var(--violet)" }}>{resetMsg}</div>}
      </div>

      {/* Create new session */}
      <div style={card}>
        <div style={eyebrow}>New session</div>
        <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>
          Session Name
          <input style={{ ...field, marginTop: 4 }} placeholder={DEFAULT_WORKSHOP.client} value={client} onChange={(e) => setClient(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>
          Session label
          <input style={{ ...field, marginTop: 4 }} value={sessionLabel} onChange={(e) => setSessionLabel(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>
          Meeting join link
          <input
            style={{ ...field, marginTop: 4 }}
            type="url"
            placeholder="https://teams.microsoft.com/l/meetup-join/…"
            value={meetingLink}
            onChange={(e) => setMeetingLink(e.target.value)}
          />
        </label>
        <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>
          Expected participants
          <input
            style={{ ...field, marginTop: 4 }}
            type="number"
            min={1}
            placeholder="e.g. 28"
            value={expected}
            onChange={(e) => setExpected(e.target.value.replace(/\D/g, ""))}
          />
        </label>
        {err && <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}
        <button className="btn-primary" onClick={create} disabled={busy}>
          {busy ? "Creating…" : "Create session →"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 18, marginTop: 18 }}>
        <Link to="/" style={{ fontSize: 12, color: "var(--muted)", textDecoration: "none" }}>
          ← Back to start
        </Link>
        <button
          onClick={lock}
          style={{ fontSize: 12, color: "var(--muted)", background: "none", border: "none", cursor: "pointer" }}
        >
          Lock admin
        </button>
      </div>
    </div>
  );
}
